import { z } from "zod";

import {
  ageRangeEnumSchema,
  childCaregiverStatusEnumSchema,
  citizenshipStatusEnumSchema,
  maritalStatusEnumSchema,
  medicalExpenses3MonthsHelpNeededStatusEnumSchema,
  patientsApiContract,
  payFrequencyEnumSchema,
  relationshipEnumSchema,
  taxClaimedAsDependentStatusEnumSchema,
  taxDependentsClaimedStatusEnumSchema,
  taxFilingStatusEnumSchema,
  taxRelationshipEnumSchema,
  unemploymentCompensationStatusEnumSchema,
} from "@libs/api/patients";
import { HTTPStatusCode } from "@libs/api/types";
import { i18n, I18nKeyType } from "@/i18n";
import { isFutureDate } from "@/utils/date";

import { coerceMissingToEmptyString, fallbackToEmptyString } from "./common";

export const dateOfBirthSchema = z.iso
  .date({
    message: i18n.t(
      "native:addHouseholdMemberScreen.validations.invalidDateOfBirth",
    ),
  })
  .refine(value => !isFutureDate(value), {
    message: i18n.t(
      "native:addHouseholdMemberScreen.validations.futureDateOfBirth",
    ),
  });

export const householdMemberSchema =
  patientsApiContract.householdMembers.getHouseholdMember.responses[
    HTTPStatusCode.OK
  ];
export type HouseholdMemberType = z.infer<typeof householdMemberSchema>;

export const householdMemberWithRelationshipListSchema =
  patientsApiContract.householdMembers.listHouseholdMembers.responses[
    HTTPStatusCode.OK
  ];
export type HouseholdMemberWithRelationshipType = z.infer<
  typeof householdMemberWithRelationshipListSchema
>[number];

export const householdMemberUpdateSchema =
  patientsApiContract.householdMembers.updateHouseholdMember.body;
export type HouseholdMemberUpdateType = z.infer<
  typeof householdMemberUpdateSchema
>;

export type AddressUpdateType = "home" | "mailing" | "homeAndMailing";

export type AddressComponentType = {
  long_name: string;
  short_name: string;
  types: string[];
};

const addressComponentSchema = z.object({
  long_name: z.string(),
  short_name: z.string(),
  types: z.array(z.string()),
});

const requiredRelationshipSchema = z.enum(relationshipEnumSchema.options, {
  message: i18n.t("native:addHouseholdMemberScreen.validations.selectAnOption"),
});

export const householdMemberRelationshipFieldsSchema = z.object({
  relationship: requiredRelationshipSchema,
  otherRelationship: z.string().optional(),
});
export type HouseholdMemberRelationshipFieldsType = z.infer<
  typeof householdMemberRelationshipFieldsSchema
>;

export const validateOtherRelationship = (
  data: {
    householdMemberRelationship?: {
      relationship: string;
      otherRelationship?: string;
    };
  },
  ctx: z.RefinementCtx,
) => {
  if (!data.householdMemberRelationship) return;

  if (
    data.householdMemberRelationship.relationship === "OTHER" &&
    (!data.householdMemberRelationship.otherRelationship ||
      data.householdMemberRelationship.otherRelationship.trim() === "")
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["householdMemberRelationship", "otherRelationship"],
      message: i18n.t(
        "native:addHouseholdMemberScreen.validations.specifyRelationship",
      ),
    });
  }
};

const SSN_KNOWN_INVALID_VALUES = ["123456789", "987654321"];

export const isValidSsn = (ssn: string): boolean => {
  if (!/^\d{9}$/.test(ssn)) return false;

  const area = ssn.slice(0, 3);
  const group = ssn.slice(3, 5);
  const serial = ssn.slice(5);

  if (area === "000" || area === "666" || area.startsWith("9")) return false;
  if (group === "00") return false;
  if (serial === "0000") return false;
  // Repeated digits (e.g. 111-11-1111) are flagged by state guidance, not the SSA
  if (/^(\d)\1{8}$/.test(ssn)) return false;

  return !SSN_KNOWN_INVALID_VALUES.includes(ssn);
};

export const requiredSsnSchema = z
  .string()
  .regex(/^\d*$/, {
    message: i18n.t("common:validation.invalidSsn"),
  })
  .length(9, {
    message: i18n.t("common:validation.invalidSsnLength"),
  })
  .refine(isValidSsn, {
    message: i18n.t("common:validation.invalidSsnFormat"),
  });

export const optionalSsnSchema = z.preprocess(
  value => (value === "" ? null : value),
  requiredSsnSchema.nullable().optional(),
);

export const optionalEmailSchema = z
  .email({ message: i18n.t("common:errors.invalidEmail") })
  .nullable()
  .or(z.literal(""))
  .optional();

export const requiredEmailSchema = z.preprocess(
  coerceMissingToEmptyString,
  z.email({ message: i18n.t("common:errors.invalidEmail") }),
);

export const householdMemberFormBaseSchema = householdMemberUpdateSchema.extend(
  {
    firstName: fallbackToEmptyString("common:required"),
    lastName: fallbackToEmptyString("common:required"),
    phoneNumber: fallbackToEmptyString("common:required"),
    dateOfBirth: z.preprocess(coerceMissingToEmptyString, dateOfBirthSchema),
    ssn: optionalSsnSchema,
    medicaidCaseNumber: z.string().trim().nullish(),
    shouldUseDifferentMailingAddress: z.boolean(),
    hasNoMailingAddress: z.boolean(),
    mainFormattedAddress: z.string().optional(),
    mailingFormattedAddress: z.string().optional(),
    mainAddressComponents: z.array(addressComponentSchema).optional(),
    mailingAddressComponents: z.array(addressComponentSchema).optional(),
    email: optionalEmailSchema,
    householdMemberRelationship:
      householdMemberUpdateSchema.shape.householdMemberRelationship
        .unwrap()
        .unwrap()
        .extend(householdMemberRelationshipFieldsSchema.shape)
        .optional(),
  },
);

export const validateHomeAddress = (data: {
  mainAddressStreetOne?: string | null;
  mainAddressCity?: string | null;
  mainAddressState?: string | null;
  isHomeless?: boolean | null;
}) => {
  const hasMainAddress = Boolean(
    data.mainAddressStreetOne?.length &&
    data.mainAddressCity?.length &&
    data.mainAddressState?.length,
  );
  return hasMainAddress || Boolean(data.isHomeless);
};

export const validateMailingAddress = (data: {
  mailingAddressStreetOne?: string | null;
  mailingAddressCity?: string | null;
  mailingAddressState?: string | null;
  isHomeless?: boolean | null;
  hasNoMailingAddress?: boolean | null;
  shouldUseDifferentMailingAddress?: boolean | null;
}) => {
  if (data.hasNoMailingAddress) return true;
  if (data.isHomeless) {
    return Boolean(
      data.mailingAddressStreetOne?.length &&
      data.mailingAddressCity?.length &&
      data.mailingAddressState?.length,
    );
  }
  if (!data.shouldUseDifferentMailingAddress) return true;
  return Boolean(
    data.mailingAddressStreetOne?.length &&
    data.mailingAddressCity?.length &&
    data.mailingAddressState?.length,
  );
};

export const homeAddressRefinement = {
  message: i18n.t("common:errors.emptyHomeAddress"),
  path: ["mainFormattedAddress"] as [string],
};

export const mailingAddressRefinement = {
  message: i18n.t("common:errors.emptyMailingAddress"),
  path: ["mailingFormattedAddress"] as [string],
};

export const householdMemberFormSchema = householdMemberFormBaseSchema
  .superRefine(validateOtherRelationship)
  .refine(validateHomeAddress, homeAddressRefinement)
  .refine(validateMailingAddress, mailingAddressRefinement);

export const coverageCheckFormSchema = householdMemberUpdateSchema
  .pick({
    ssn: true,
    medicaidSubscriberId: true,
  })
  .extend({
    ssn: optionalSsnSchema,
    medicaidSubscriberId: z.string().optional(),
  });

export type HouseholdMemberFormType = z.infer<typeof householdMemberFormSchema>;
export type CoverageCheckFormType = z.infer<typeof coverageCheckFormSchema>;

export type AddressFieldsType = {
  streetOne?: string | null;
  streetTwo?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
};

export const addHouseholdMemberAndRelationshipSchema =
  patientsApiContract.householdMembers.addHouseholdMember.body;

export type AddHouseholdMemberAndRelationshipType = z.infer<
  typeof addHouseholdMemberAndRelationshipSchema
>;

export const householdMemberAndRelationshipFormSchema =
  householdMemberUpdateSchema
    .extend({
      firstName: z.string().min(1, {
        message: i18n.t("common:required"),
      }),
      lastName: z.string().min(1, {
        message: i18n.t("common:required"),
      }),
      dateOfBirth: dateOfBirthSchema.nullish(),
      ageRange: ageRangeEnumSchema.nullish(),
      ssn: optionalSsnSchema,
      sameMailingAddress: z.boolean().default(true),
      childCaregiverStatus: childCaregiverStatusEnumSchema.nullish(),
      householdMemberRelationship:
        householdMemberUpdateSchema.shape.householdMemberRelationship
          .unwrap()
          .unwrap()
          .extend({
            ...householdMemberRelationshipFieldsSchema.shape,
            livingWithStatus: z.boolean().default(true),
            taxRelationship: taxRelationshipEnumSchema.nullable(),
          }),
    })
    .superRefine(validateOtherRelationship);
export type HouseholdMemberAndRelationshipFormType = z.infer<
  typeof householdMemberAndRelationshipFormSchema
>;

export type HouseholdMemberRelationsType = z.infer<
  typeof relationshipEnumSchema
>;
export const householdMemberRelations = relationshipEnumSchema.options.map(
  option => ({
    label: i18n.t(`common:relationships.${option}` as I18nKeyType),
    value: option,
  }),
);

export type HouseholdMemberAgeRangeType = z.infer<typeof ageRangeEnumSchema>;
export const householdMemberAges = ageRangeEnumSchema.options.map(option => ({
  label: i18n.t(`common:ages.${option}` as I18nKeyType),
  value: option,
}));

export const householdMemberMaritalStatus = maritalStatusEnumSchema.options.map(
  option => ({
    label: i18n.t(`common:maritalStatus.${option}` as I18nKeyType),
    value: option,
  }),
);

export const householdMemberTaxFilingStatus =
  taxFilingStatusEnumSchema.options.map(option => ({
    label: i18n.t(`common:taxFilingStatus.${option}` as I18nKeyType),
    value: option,
  }));

export const householdMemberTaxDependentsClaimedStatus =
  taxDependentsClaimedStatusEnumSchema.options.map(option => ({
    label:
      option === taxDependentsClaimedStatusEnumSchema.enum.NOT_CLAIMED
        ? i18n.t("common:no")
        : i18n.t("common:yes"),
    value: option,
  }));

export const householdMemberTaxClaimedAsDependentStatus =
  taxClaimedAsDependentStatusEnumSchema.options.map(option => ({
    label:
      option ===
      taxClaimedAsDependentStatusEnumSchema.enum.NOT_CLAIMED_AS_DEPENDENT
        ? i18n.t("common:no")
        : i18n.t("common:yes"),
    value: option,
  }));

{
  /* Household Member Income and Expenses Starts*/
}
// Income fields
export const householdMemberPayFrequency = payFrequencyEnumSchema.options.map(
  option => ({
    label: i18n.t(`common:payFrequency.${option}` as I18nKeyType),
    value: option,
  }),
);

export const householdMemberUnemploymentCompensationStatus =
  unemploymentCompensationStatusEnumSchema.options.map(option => ({
    label:
      option ===
      unemploymentCompensationStatusEnumSchema.enum.UNEMPLOYMENT_COMPENSATED
        ? i18n.t("common:yes")
        : i18n.t("common:no"),
    value: option,
  }));

export enum IncomeTypeEnum {
  EMPLOYED = "EMPLOYED",
  SELF_EMPLOYED = "SELF_EMPLOYED",
  UNEMPLOYED = "UNEMPLOYED",
  SSA = "SSA",
  SSDI = "SSDI",
  PENSION = "PENSION",
  ALIMONY = "ALIMONY",
  RETIREMENT_INCOME = "RETIREMENT_INCOME",
  PROPERTY_RENTAL_INCOME = "PROPERTY_RENTAL_INCOME",
  WORKERS_COMPENSATION = "WORKERS_COMPENSATION",
  OTHER = "OTHER",
  NONE = "NONE",
}

export const getIncomeTypeI18nKey = (
  incomeType: IncomeTypeEnum,
): I18nKeyType => {
  return `native:applicationWorkAndIncomeScreen.incomeTypes.${incomeType}` as I18nKeyType;
};

export const incomeTypeOptions: {
  value: IncomeTypeEnum;
  i18nKey: I18nKeyType;
}[] = Object.values(IncomeTypeEnum).map(value => ({
  value,
  i18nKey: getIncomeTypeI18nKey(value),
}));

// List of fields that need conversion from string to number
export const INCOME_NUMERIC_FIELDS = [
  "grossMonthlyIncome",
  "unemploymentCompensationAmount",
  "selfEmploymentNetMonthlyIncome",
  "pensionMonthlyIncome",
  "ssaMonthlyIncome",
  "ssdiMonthlyIncome",
  "alimonyMonthlyIncome",
  "retirementMonthlyIncome",
  "propertyRentalMonthlyIncome",
  "workersCompensationMonthlyIncome",
  "otherMonthlyIncome",
] as const;

export const householdMemberWorkAndIncomeFormSchema =
  householdMemberUpdateSchema
    .pick({
      employmentStatus: true,
      workWeekHours: true,
      payFrequency: true,
      employerName: true,
      unemploymentCompensationStatus: true,
      employmentStatusSelfEmployedDetails: true,
      grossMonthlyIncome: true,
      unemploymentCompensationAmount: true,
      selfEmploymentNetMonthlyIncome: true,
      pensionMonthlyIncome: true,
      ssaMonthlyIncome: true,
      ssdiMonthlyIncome: true,
      alimonyMonthlyIncome: true,
      retirementMonthlyIncome: true,
      propertyRentalMonthlyIncome: true,
      workersCompensationMonthlyIncome: true,
      otherMonthlyIncome: true,
      hasIncome: true,
    })
    .extend({
      grossMonthlyIncome: z.string().nullable(),
      unemploymentCompensationAmount: z.string().nullable(),
      selfEmploymentNetMonthlyIncome: z.string().nullable(),
      pensionMonthlyIncome: z.string().nullable(),
      ssaMonthlyIncome: z.string().nullable(),
      ssdiMonthlyIncome: z.string().nullable(),
      alimonyMonthlyIncome: z.string().nullable(),
      retirementMonthlyIncome: z.string().nullable(),
      propertyRentalMonthlyIncome: z.string().nullable(),
      workersCompensationMonthlyIncome: z.string().nullable(),
      otherMonthlyIncome: z.string().nullable(),
    });

export type HouseholdMemberWorkAndIncomeFormType = z.infer<
  typeof householdMemberWorkAndIncomeFormSchema
>;

export const createEmptyIncomeData = () => ({
  employmentStatus: null,
  workWeekHours: null,
  payFrequency: null,
  employerName: null,
  unemploymentCompensationStatus: null,
  employmentStatusSelfEmployedDetails: null,
  grossMonthlyIncome: null,
  unemploymentCompensationAmount: null,
  selfEmploymentNetMonthlyIncome: null,
  pensionMonthlyIncome: null,
  ssaMonthlyIncome: null,
  ssdiMonthlyIncome: null,
  alimonyMonthlyIncome: null,
  retirementMonthlyIncome: null,
  propertyRentalMonthlyIncome: null,
  workersCompensationMonthlyIncome: null,
  otherMonthlyIncome: null,
  hasIncome: false,
});

export const employedFormSchema = householdMemberWorkAndIncomeFormSchema
  .pick({
    employmentStatus: true,
    workWeekHours: true,
    payFrequency: true,
    employerName: true,
    grossMonthlyIncome: true,
  })
  .extend({
    grossMonthlyIncome: z
      .string()
      .min(1, { message: i18n.t("common:required") }),
    workWeekHours: z.string().min(1, { message: i18n.t("common:required") }),
    payFrequency: payFrequencyEnumSchema,
    employerName: z
      .string()
      .trim()
      .min(1, { message: i18n.t("common:required") }),
  });

export type EmployedFormType = z.infer<typeof employedFormSchema>;

export type UnemployedFormType = Pick<
  HouseholdMemberWorkAndIncomeFormType,
  | "employmentStatus"
  | "unemploymentCompensationStatus"
  | "unemploymentCompensationAmount"
>;

export const unemployedFormSchema = householdMemberWorkAndIncomeFormSchema.pick(
  {
    employmentStatus: true,
    unemploymentCompensationStatus: true,
    unemploymentCompensationAmount: true,
  },
);

export const selfEmployedFormSchema = householdMemberWorkAndIncomeFormSchema
  .pick({
    employmentStatus: true,
    employmentStatusSelfEmployedDetails: true,
    selfEmploymentNetMonthlyIncome: true,
  })
  .extend({
    selfEmploymentNetMonthlyIncome: z
      .string()
      .min(1, { message: i18n.t("common:required") }),
    employmentStatusSelfEmployedDetails: z
      .string()
      .trim()
      .min(1, { message: i18n.t("common:required") }),
  });

export type SelfEmployedFormType = z.infer<typeof selfEmployedFormSchema>;

export const getIncomeTypeAlertDescriptionI18nKey = (
  incomeType: IncomeTypeEnum,
): I18nKeyType => {
  if (
    incomeType !== IncomeTypeEnum.EMPLOYED &&
    incomeType !== IncomeTypeEnum.UNEMPLOYED &&
    incomeType !== IncomeTypeEnum.SELF_EMPLOYED
  ) {
    return "native:applicationWorkAndIncomeScreen.memberDialog.alertDescriptions.OTHER" as I18nKeyType;
  }
  return `native:applicationWorkAndIncomeScreen.memberDialog.alertDescriptions.${incomeType}` as I18nKeyType;
};

// Expense fields
export const householdMemberMedicalExpenses3MonthsHelpNeededStatus =
  medicalExpenses3MonthsHelpNeededStatusEnumSchema.options.map(option => ({
    label:
      option ===
      medicalExpenses3MonthsHelpNeededStatusEnumSchema.enum.MEDICAL_EXPENSES
        ? i18n.t("common:yes")
        : i18n.t("common:no"),
    value: option,
  }));

export enum ExpenseTypeEnum {
  STUDENT_LOANS = "STUDENT_LOANS",
  TUITION_FEES = "TUITION_FEES",
  ALIMONY_PAID = "ALIMONY_PAID",
  MOVING_EXPENSES = "MOVING_EXPENSES",
  IRA_CONTRIBUTIONS = "IRA_CONTRIBUTIONS",
  CHILDCARE = "CHILDCARE",
  MEDICAL_EXPENSES = "MEDICAL_EXPENSES",
  OTHER = "OTHER",
  NONE = "NONE",
}

export const getExpenseTypeI18nKey = (
  expenseType: ExpenseTypeEnum,
): I18nKeyType => {
  return `native:applicationExpensesScreen.expenseTypes.${expenseType}` as I18nKeyType;
};

export const expenseTypeOptions: {
  value: ExpenseTypeEnum;
  i18nKey: I18nKeyType;
}[] = Object.values(ExpenseTypeEnum).map(value => ({
  value,
  i18nKey: getExpenseTypeI18nKey(value),
}));

export const EXPENSE_NUMERIC_FIELDS = [
  "alimonyPaidMonthlyExpense",
  "iraContributionsMonthlyExpense",
  "movingMonthlyExpense",
  "childcareMonthlyExpense",
  "otherMonthlyExpense",
  "studentLoansMonthlyExpense",
  "tuitionFeesMonthlyExpense",
] as const;

export const householdMemberExpensesFormSchema = householdMemberUpdateSchema
  .pick({
    alimonyPaidMonthlyExpense: true,
    iraContributionsMonthlyExpense: true,
    movingMonthlyExpense: true,
    childcareMonthlyExpense: true,
    otherMonthlyExpense: true,
    studentLoansMonthlyExpense: true,
    tuitionFeesMonthlyExpense: true,
    medicalExpenses3MonthsHelpNeededStatus: true,
    hasExpense: true,
  })
  .extend({
    alimonyPaidMonthlyExpense: z.string().nullable(),
    iraContributionsMonthlyExpense: z.string().nullable(),
    movingMonthlyExpense: z.string().nullable(),
    childcareMonthlyExpense: z.string().nullable(),
    otherMonthlyExpense: z.string().nullable(),
    studentLoansMonthlyExpense: z.string().nullable(),
    tuitionFeesMonthlyExpense: z.string().nullable(),
  });

export type HouseholdMemberExpensesFormType = z.infer<
  typeof householdMemberExpensesFormSchema
>;

export const createEmptyExpensesData = () => ({
  alimonyPaidMonthlyExpense: null,
  iraContributionsMonthlyExpense: null,
  movingMonthlyExpense: null,
  childcareMonthlyExpense: null,
  otherMonthlyExpense: null,
  studentLoansMonthlyExpense: null,
  tuitionFeesMonthlyExpense: null,
  medicalExpenses3MonthsHelpNeededStatus: null,
  hasExpense: false,
});

export type MedicalExpensesFormType = Pick<
  HouseholdMemberExpensesFormType,
  "medicalExpenses3MonthsHelpNeededStatus"
>;

export const medicalExpensesFormSchema = householdMemberExpensesFormSchema.pick(
  {
    medicalExpenses3MonthsHelpNeededStatus: true,
  },
);

export const getExpenseTypeAlertDescriptionI18nKey = (
  expenseType: ExpenseTypeEnum,
): I18nKeyType => {
  if (expenseType === ExpenseTypeEnum.MEDICAL_EXPENSES) {
    return "native:applicationExpensesScreen.memberDialog.alertDescriptions.MEDICAL_EXPENSES" as I18nKeyType;
  }
  if (expenseType === ExpenseTypeEnum.STUDENT_LOANS) {
    return "native:applicationExpensesScreen.memberDialog.alertDescriptions.STUDENT_LOANS" as I18nKeyType;
  }
  return "native:applicationExpensesScreen.memberDialog.alertDescriptions.OTHER" as I18nKeyType;
};
{
  /* Household Member Income and Expenses Ends*/
}

export const householdMemberAddressFormSchema = householdMemberUpdateSchema
  .pick({
    mainAddressStreetOne: true,
    mainAddressStreetTwo: true,
    mainAddressComplement: true,
    mainAddressCity: true,
    mainAddressState: true,
    mainAddressZip: true,
    mainCounty: true,
    mailingAddressStreetOne: true,
    mailingAddressStreetTwo: true,
    mailingAddressComplement: true,
    mailingAddressCity: true,
    mailingAddressState: true,
    mailingAddressZip: true,
    mailingCounty: true,
    isHomeless: true,
    hasNoMailingAddress: true,
  })
  .extend({
    isHomeless: z.boolean().default(false),
    hasNoMailingAddress: z.boolean().default(false),
    mainFormattedAddress: z.string().optional(),
    mailingFormattedAddress: z.string().optional(),
  })
  .refine(validateHomeAddress, homeAddressRefinement)
  .refine(data => {
    if (data.hasNoMailingAddress) return true;
    return Boolean(
      data.mailingAddressStreetOne?.length &&
      data.mailingAddressCity?.length &&
      data.mailingAddressState?.length,
    );
  }, mailingAddressRefinement);

export type HouseholdMemberAddressFormType = z.infer<
  typeof householdMemberAddressFormSchema
>;

export const householdMemberCitizenshipStatus =
  citizenshipStatusEnumSchema.options.map(option => ({
    label: i18n.t(`common:citizenshipStatus.${option}`),
    value: option,
  }));
